import React, { Fragment, ReactNode } from "react";
import { NavLink, useHistory } from "react-router-dom";
import { useActiveSubtab } from "./SubtabUtils";
import { ButtonVariant, Divider, Menu, MenuItem } from "@merge-api/merge-javascript-shared";
import { ChevronDown } from "lucide-react";
import SubLeftNavigationBarCard, {
  SubLeftNavigationBarCardProps,
} from "./SubLeftNavigationBarCard";

export interface Subtab {
  /**
   * If provided, a react element icon to show to the left of the label
   */
  icon?: React.ReactElement;
  label: string;
  destination: string;
  divider?: boolean;
  isActive?: boolean;
}

interface Props {
  subtabs?: Array<Subtab>;
  cards?: Array<SubLeftNavigationBarCardProps>;
  children: ReactNode;
}

function SubLeftNavigationDropdown({ subtabs }: { subtabs: Array<Subtab> }) {
  const activeSubtab = useActiveSubtab(subtabs);
  const history = useHistory();

  return (
    <Menu
      menuPlacement="bottom-start"
      ButtonProps={{
        children: (
          <div className="flex flex-row gap-x-1 items-center justify-start">
            {activeSubtab?.icon} {activeSubtab?.label} <ChevronDown size={16} />
          </div>
        ),
        variant: ButtonVariant.SecondaryGray,
      }}
    >
      {subtabs.map((subtab) => (
        <MenuItem
          onClick={() => history.push(subtab.destination)}
          disabled={subtab === activeSubtab}
          key={subtab.label}
        >
          <div className="flex flex-row gap-x-1 items-center justify-start">
            {subtab.icon} {subtab.label}
          </div>
        </MenuItem>
      ))}
    </Menu>
  );
}

const SubLeftNavigationBar = ({ subtabs }: { subtabs?: Array<Subtab> }) => (
  <ul className="navbar-nav sub-navbar-nav">
    {subtabs &&
      subtabs.map((subtab) => {
        return (
          <Fragment key={subtab.label}>
            {subtab.divider && (
              <li>
                <hr className="my-3" />
              </li>
            )}
            <li className="nav-item">
              <NavLink
                className={`nav-link sub-nav-link flex items-center justify-start text-left ${
                  subtab?.isActive ? "active" : ""
                }`}
                to={subtab.destination}
              >
                {subtab.icon}
                {subtab.label}
              </NavLink>
            </li>
          </Fragment>
        );
      })}
  </ul>
);

const SubLeftNavigationBarCards = ({
  cardsProps,
}: {
  cardsProps: Array<SubLeftNavigationBarCardProps>;
}) => {
  const divider = cardsProps.length > 0 ? <Divider className="mt-[14px] mb-[22px]" /> : null;
  const cards = (
    <div>
      {cardsProps &&
        cardsProps.map((cardProps) => {
          return <SubLeftNavigationBarCard {...cardProps} />;
        })}
    </div>
  );

  return (
    <>
      {divider}
      {cards}
    </>
  );
};

const SubLeftNavigationContainer = ({ subtabs, children, cards }: Props) => {
  return (
    <div className="flex justify-center">
      <div className="w-full">
        {subtabs && (
          <div className="flex lg:hidden mb-5">
            <SubLeftNavigationDropdown subtabs={subtabs} />
          </div>
        )}
        <div className="flex w-full gap-8">
          <div className="hidden lg:block w-3/12">
            <SubLeftNavigationBar subtabs={subtabs} />
            <SubLeftNavigationBarCards cardsProps={cards ?? []} />
          </div>
          <div className="w-full lg:flex-1 min-w-0">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default SubLeftNavigationContainer;
